import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import SetupGuideModal from "./SetupGuideModal";
import PoliticalProfileTab from "./PoliticalProfileTab";
import PoliticalAchievementsTab from "./PoliticalAchievementsTab";
import PoliticalActivitiesTab from "./PoliticalActivitiesTab";
import PoliticalGalleryTab from "./PoliticalGalleryTab";
import PoliticalContactTab from "./PoliticalContactTab";
import PoliticalSocialTab from "./PoliticalSocialTab";
import PoliticalThemeTab from "./PoliticalThemeTab";
import PoliticalPaymentsTab from "./PoliticalPaymentsTab";
import PoliticalInboxTab from "./PoliticalInboxTab";

/*
  Dashboard page to manage the customer's Political Website.
  Tabs: Profile / Achievements & Timeline / Activities / Gallery & Videos /
        Contact Section & Office / Donation QR / Social Links /
        Website Theme Colour / Inbox.
  Everything except the Inbox flows through one shared "Save Website" action.
  props: userData, listinguid, onBack
*/

const emptyAchievement = { title: "", title_np: "", year: "", description: "", description_np: "", icon: "bi-award-fill", category: "political" };
const emptyTimelineEntry = { year: "", title: "", title_np: "", description: "", description_np: "" };
const YT_RE = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i;

const blankForm = {
    name: "", name_np: "",
    position_title: "", position_title_np: "",
    party: "", party_np: "", party_logo: "",
    tagline: "", tagline_np: "",
    photo: "", cover: "",
    phone: "", email: "", address: "", website: "",
    about: "", about_np: "",
    office_title: "", office_title_np: "",
    min_donation: 100,
    linkfacebook: "", linkinstagram: "", linktwitter: "", linklinkedin: "", youtube_link: "",
    theme_color: "classic-navy",
};

export default function PoliticalWebsiteManager({ userData, listinguid, onBack }) {
    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
    const personaluid = userData?.personaluid || localUser?.personaluid;

    const [tab, setTab] = useState("profile");
    const [form, setForm] = useState({ ...blankForm });
    const [achievements, setAchievements] = useState([{ ...emptyAchievement }]);
    const [timeline, setTimeline] = useState([{ ...emptyTimelineEntry }]);
    const [stats, setStats] = useState([]);
    const [facts, setFacts] = useState([]);
    const [secretary, setSecretary] = useState({});
    const [activities, setActivities] = useState([]);
    const [gallery, setGallery] = useState([{ url: "", title: "", title_np: "", category: "public" }]);
    const [videos, setVideos] = useState([]);
    const [payments, setPayments] = useState([]);
    const [domain, setDomain] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    // Load data on mount
    useEffect(() => {
        if (!personaluid) return;
        fetch(`${API_BASE}/api/get_political_site.php?personaluid=${encodeURIComponent(personaluid)}`)
            .then((r) => r.json())
            .then((res) => {
                if (res.domain) setDomain(res.domain);
                if (res.success && res.data) {
                    const d = res.data;
                    const next = { ...blankForm };
                    Object.keys(blankForm).forEach((k) => { if (d[k] !== undefined && d[k] !== null) next[k] = d[k]; });
                    setForm(next);
                    setAchievements(d.achievements?.length ? d.achievements : [{ ...emptyAchievement }]);
                    setTimeline(d.timeline?.length ? d.timeline : [{ ...emptyTimelineEntry }]);
                    setStats(d.stats || []);
                    setFacts(d.facts || []);
                    setSecretary(d.secretary && !Array.isArray(d.secretary) ? d.secretary : {});
                    setActivities(d.activities || []);
                    setVideos(d.videos || []);
                    setPayments(d.payment_methods || []);
                    const items = d.gallery_items?.length
                        ? d.gallery_items
                        : (d.gallery || []).map((url) => ({ url, title: "", title_np: "", category: "public" }));
                    setGallery(items.length ? items : [{ url: "", title: "", title_np: "", category: "public" }]);
                }
            })
            .catch(() => { });
    }, [personaluid]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const handleSave = async () => {
        if (!(form.name || "").trim()) {
            setMsg({ type: "warning", text: "Full name is required." });
            setTab("profile");
            return;
        }

        const yt = (form.youtube_link || "").trim();
        if (yt && !YT_RE.test(yt)) {
            setMsg({ type: "warning", text: "YouTube field must be a valid YouTube link." });
            setTab("social");
            return;
        }

        const cleanVideos = videos.filter((v) => (v.url || "").trim() !== "");
        const badVideo = cleanVideos.find((v) => !YT_RE.test(v.url.trim()) && !/^https?:\/\//i.test(v.url.trim()));
        if (badVideo) {
            setMsg({ type: "warning", text: "Each video needs a YouTube link or an uploaded file." });
            setTab("gallery");
            return;
        }

        // an entry with only a name or only an image would be silently dropped on save, so flag it instead
        const halfMethod = payments.find((m) => {
            const hasName = (m.name || "").trim() !== "";
            const hasQr = (m.qr || "").trim() !== "";
            return hasName !== hasQr;
        });
        if (halfMethod) {
            setMsg({ type: "warning", text: "Each donation QR needs both a name and an image." });
            setTab("payments");
            return;
        }

        setSaving(true);
        setMsg(null);
        try {
            const res = await fetch(`${API_BASE}/api/save_political_site.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    personaluid,
                    listinguid,
                    ...form,
                    min_donation: Math.max(10, parseInt(form.min_donation, 10) || 100),
                    youtube_link: yt,
                    achievements: achievements.filter((a) => (a.title || "").trim() !== ""),
                    timeline: timeline.filter((t) => (t.title || "").trim() !== ""),
                    stats: stats.filter((s) => (s.value || "").trim() !== ""),
                    facts: facts.filter((f) => (f.value || "").trim() !== ""),
                    secretary,
                    activities: activities.filter((a) => (a.title || "").trim() !== ""),
                    videos: cleanVideos,
                    payment_methods: payments.filter((m) => (m.name || "").trim() !== "" && (m.qr || "").trim() !== ""),
                    gallery: gallery.filter((g) => (g.url || "").trim() !== ""),
                }),
            });
            const data = await res.json();
            setSaving(false);
            if (data.success) {
                setDomain(data.domain);
                setMsg({ type: "success", text: "Political website saved successfully." });
            } else {
                setMsg({ type: "danger", text: data.message || "Could not save." });
            }
        } catch {
            setSaving(false);
            setMsg({ type: "danger", text: "Network error while saving." });
        }
    };

    const tabs = [
        ["profile", "Profile"],
        ["achievements", "Achievements"],
        ["activities", "Activities"],
        ["gallery", "Gallery & Videos"],
        ["contact", "Contact & Office"],
        ["payments", "Donation QR"],
        ["social", "Social Links"],
        ["theme", "Theme Colour"],
        ["inbox", "Inbox"],
    ];

    return (
        <div className="container-fluid py-2" style={{ maxWidth: 1120 }}>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => onBack && onBack()}>
                    <i className="bi bi-arrow-left"></i> Back
                </button>
                <h5 className="fw-bold mb-0">Political Website</h5>
                <div className="d-flex align-items-center gap-2">
                    <SetupGuideModal
                        storageKey={`dn_guide_political_${personaluid || "me"}`}
                        title="Set up your Political Website"
                        intro="Build your public political profile. Fill these sections to launch your website."
                        steps={[
                            { title: "Profile", desc: "Names in English and Nepali, position, party, hero stats, quick facts, biography, photos and office contact." },
                            { title: "Achievements", desc: "Achievements (with icon and category) and your career timeline." },
                            { title: "Activities", desc: "Upcoming and completed events shown with filters and counts." },
                            { title: "Gallery & Videos", desc: "Categorised photos with captions, and videos (YouTube or uploaded)." },
                            { title: "Contact & Office", desc: "Appointment card heading and the personal-secretary / office contact shown in the footer." },
                            { title: "Donation QR", desc: "Upload a QR image and name for each payment method (eSewa, Khalti, FonePay…). Visitors pick a method and the QR changes." },
                            { title: "Social Links", desc: "Facebook, Instagram, X, LinkedIn and YouTube links shown as footer icons." },
                            { title: "Theme Colour", desc: "Choose a ready-made theme or pick your own main and accent colours." },
                            { title: "Inbox", desc: "Messages, appointment requests and donations sent from your website. Verify donations and update appointment status here." },
                        ]}
                    />
                    {domain && (
                        <a className="btn btn-sm btn-outline-success" target="_blank" rel="noreferrer" href={`/${domain}`}>
                            <i className="bi bi-box-arrow-up-right me-1" /> View Live Website
                        </a>
                    )}
                </div>
            </div>

            <ul className="nav nav-tabs mb-3 flex-nowrap overflow-auto">
                {tabs.map(([k, label]) => (
                    <li className="nav-item" key={k}>
                        <button className={`nav-link text-nowrap ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} type="button">
                            {label}
                        </button>
                    </li>
                ))}
            </ul>

            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

            {tab === "profile" && (
                <PoliticalProfileTab
                    form={form} set={set} personaluid={personaluid}
                    stats={stats} setStats={setStats}
                    facts={facts} setFacts={setFacts}
                />
            )}
            {tab === "achievements" && (
                <PoliticalAchievementsTab achievements={achievements} setAchievements={setAchievements} timeline={timeline} setTimeline={setTimeline} />
            )}
            {tab === "activities" && <PoliticalActivitiesTab activities={activities} setActivities={setActivities} />}
            {tab === "gallery" && (
                <PoliticalGalleryTab
                    gallery={gallery} setGallery={setGallery}
                    videos={videos} setVideos={setVideos}
                    personaluid={personaluid}
                />
            )}
            {tab === "contact" && (
                <PoliticalContactTab form={form} set={set} secretary={secretary} setSecretary={setSecretary} personaluid={personaluid} />
            )}
            {tab === "social" && <PoliticalSocialTab form={form} set={set} />}
            {tab === "theme" && <PoliticalThemeTab form={form} set={set} />}
            {tab === "payments" && (
                <PoliticalPaymentsTab methods={payments} setMethods={setPayments} form={form} set={set} personaluid={personaluid} />
            )}
            {tab === "inbox" && <PoliticalInboxTab personaluid={personaluid} />}

            {tab !== "inbox" && (
                <div className="d-flex justify-content-end gap-2 mb-4 mt-4">
                    <button className="btn btn-primary px-4" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving..." : "Save Website"}
                    </button>
                </div>
            )}
        </div>
    );
}